import { Button } from "@src/components/ui-components/Buttons/Button/Button";
import { useRealtimeUserByID } from "@src/data-hooks/user/user.service";
import { useActionValidatorRight } from "@src/auth/useActionValidatorRights";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ContractorGroupData } from "../../../to-be-removed/LocalData/ContractorGroup/ContractorGroupData";
import { LocalData } from "../../../to-be-removed/LocalData/General/LocalData";
import { UserData } from "../../../to-be-removed/LocalData/User/UserData";
import { LoadingIndicator } from "../../ui-components/LoadingIndicator/LoadingIndicator";
import { SectionSplitterLabel } from "../../ui-components/SectionSplitterLabel/SectionSplitterLabel";
import { Stack } from "../../ui-components/Stack/Stack";
import { Label } from "../Label/Label";
import { Popup } from "../Popup/Popup";
import { NewRightFullHeightPopup } from "../RightFullHeight/RightFullHeightPopUp";
import { Text } from "../Text/Text";
import { UserImageView } from "../UserImageView/UserImageView";
import styles from "./UserPopUp.module.css";
import { eventBus } from "@src/utils/eventBus";
import { EventBusKey } from "@src/utils/eventBusEnum";
import { AriaButton } from "@src/components/ui-components/Buttons/AriaButton/AriaButton";

function getUserContractorGroups(user: UserData): ContractorGroupData[] {
  const properties = user.properties;
  const contractorGroups: ContractorGroupData[] = properties.flatMap((prop) => prop.contractors).filter((prop) => !prop.deleted);

  contractorGroups.push(...user.contractorGroups.filter((cGroups) => !cGroups.deleted && !contractorGroups.includes(cGroups)));

  return contractorGroups;
}

function getSharedContractorGroups(user: UserData) {
  const currUserCGroups = getUserContractorGroups(LocalData.currentUser);
  const focusedUserCGroups = user.contractorGroups;

  return currUserCGroups
    .filter((cGroup) => focusedUserCGroups.includes(cGroup))
    .filter((group, index) => currUserCGroups.indexOf(group) === index)
    .sort((a, b) => a.name.localeCompare(b.name));
}

interface UserPopUpProps {
  id: string;
  user: UserData;
}

export const UserPopUp = (props: UserPopUpProps) => {
  const userID = props.user.id;
  const { data: user } = useRealtimeUserByID(props.user.id);

  const sharedCompanies = props.user.companies.filter((company) => LocalData.currentUser.companies.includes(company));
  const sharedContractorGroups = getSharedContractorGroups(props.user);
  const sharedProperties = props.user.properties
    .filter((property) => LocalData.currentUser.properties.includes(property))
    .sort((a, b) => a.name.localeCompare(b.name));

  const [loaded, setLoaded] = useState(props.user.loaded);
  const [, setLastUpdate] = useState(new Date());

  const usersPageAccess = useActionValidatorRight("userspage_access", {});

  useEffect(() => {
    setLoaded(props.user.loaded);
    props.user.setupRealtimeListener(() => {
      setLoaded(true);
      setLastUpdate(new Date());
      loadContractorGroups();
    });
    loadContractorGroups();
  });

  function loadContractorGroups() {
    sharedContractorGroups
      .filter((cGroup) => !cGroup.loaded)
      .forEach((cGroup) => {
        cGroup.loadWithCompletionHandler((err) => {
          if (err) console.log(err);
          setLastUpdate(new Date());
        });
      });
  }

  function pushPath(path: string) {
    Popup.remove(props.id);
    eventBus.publish(EventBusKey.Navigate, path);
  }

  return (
    <NewRightFullHeightPopup
      id={props.id}
      children={
        !loaded ? (
          <LoadingIndicator />
        ) : (
          <div id={styles.userPopUpContent}>
            <div className={styles.header}>
              <UserImageView className={styles.userImage} user={props.user} />
              <div className={styles.infoHeader}>
                <Text size="xxl" isBold>
                  {user?.name}
                </Text>
                <Label text={`${sharedProperties.length} ${t("main.shared")} ${t("main.properties")}`} type={"paragraph"} />
              </div>
            </div>

            <div className={styles.contactInfo}>
              <Stack marginBottom={16} spacing={8}>
                <AriaButton
                  onPress={() => pushPath("/chat?uId=" + userID)}
                  isDisabled={LocalData.currentUser === props.user}
                  fullWidth
                >
                  {t("main.message")}
                </AriaButton>
                {usersPageAccess && (
                  <AriaButton variant="secondary" fullWidth onPress={() => pushPath("/users/" + userID)}> 
                    {t("main.administrate")}
                  </AriaButton>
                )}
              </Stack>

              {user && (
                <>
                  <ContactInfoItem hrefType="mailto" title={t("main.email")} value={user?.email ?? ""} />
                  <ContactInfoItem hrefType="tel" title={t("main.phone")} value={user?.phone ?? ""} />
                </>
              )}
            </div>

            {sharedCompanies.length > 0 && (
              <div className={styles.clickableList}>
                <SectionSplitterLabel text={t("main.companies")} className={styles.smallHeader} />
                <ul>
                  {sharedCompanies.map((company) => (
                    <ClickableListItem key={company.id} text={company.name} handleClick={() => pushPath(`/companies/${company.id}`)} />
                  ))}
                </ul>
              </div>
            )}

            {sharedProperties.length > 0 && (
              <div className={styles.clickableList}>
                <SectionSplitterLabel text={t("main.properties")} className={styles.smallHeader} />
                <ul>
                  {sharedProperties.map((property) => (
                    <ClickableListItem key={property.id} text={property.name} handleClick={() => pushPath(`/properties/${property.id}`)} />
                  ))}
                </ul>
              </div>
            )}

            {sharedContractorGroups.length > 0 && (
              <div className={styles.clickableList}>
                <SectionSplitterLabel text={t("main.contractorGroups")} className={styles.smallHeader} />
                <ul>
                  {sharedContractorGroups.map((contractorGroup) => (
                    <ClickableListItem
                      key={contractorGroup.id}
                      text={contractorGroup.name}
                      handleClick={() => pushPath(`/contractors/${contractorGroup.id}`)}
                    />
                  ))}
                </ul>
              </div>
            )}
          </div>
        )
      }
    />
  );
};

interface ContactInfoElementProps {
  title: string;
  value: string;
  hrefType: "mailto" | "tel";
}

const ContactInfoItem = (props: ContactInfoElementProps) => {
  return (
    <>
      <Label className={styles.contactLabel} text={props.title} type="paragraph-2" />

      {props.value.length === 0 ? (
        <Label className={styles.fieldNotAvailable} text={t("main.notAvailable")} type="paragraph-2" />
      ) : (
        <a className={styles.clickableText} href={`${props.hrefType}: ${props.value}`}>
          {props.value}
          <i className="material-icons">launch</i>
        </a>
      )}
    </>
  );
};

interface ClickableListItemProps {
  text: string;
  subText?: string;
  handleClick: () => void;
}

const ClickableListItem = (props: ClickableListItemProps) => {
  return (
    <li className={styles.clickableListItem} onClick={() => props.handleClick()}>
      <Label className={styles.clickableListItemText + " " + styles.clickableText} text={props.text} type={"paragraph-2"} />
      <Label className={styles.subText} text={props.subText ? props.subText : ""} type={"paragraph-2"} color={"var(--text-2-color)"} />
      <i className="material-icons">keyboard_arrow_right</i>
    </li>
  );
};
